import { createContext, useContext, ReactNode, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import { FormErrors } from 'app/modules/filingFormGenerator/models';

interface SubmissionErrorContextValue {
  submissionErrorMap: Record<string, string>;
}

const SubmissionErrorContext =
  createContext<SubmissionErrorContextValue | null>(null);

export const SubmissionErrorProvider = ({
  children,
  submissionErrors,
}: {
  children: ReactNode;
  submissionErrors: FormErrors;
}) => {
  const prevMapRef = useRef<Record<string, string>>({});

  const submissionErrorMap = useMemo(() => {
    const next = submissionErrors.reduce<Record<string, string>>(
      (acc, error) => {
        acc[error.path] = error.message;
        return acc;
      },
      {},
    );

    if (isEqual(prevMapRef.current, next)) {
      return prevMapRef.current;
    }

    prevMapRef.current = next;
    return next;
  }, [submissionErrors]);

  const contextValue = useMemo(
    () => ({
      submissionErrorMap,
    }),
    [submissionErrorMap],
  );

  return (
    <SubmissionErrorContext.Provider value={contextValue}>
      {children}
    </SubmissionErrorContext.Provider>
  );
};

export function useSubmissionErrors() {
  const context = useContext<SubmissionErrorContextValue | null>(
    SubmissionErrorContext,
  );
  if (!context) {
    throw new Error(
      'useSubmissionErrors must be used within SubmissionErrorProvider',
    );
  }

  return context;
}
